import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Close,
  Save,
  Edit,
  Event,
  Today,
  Schedule,
  LocationOn,
  Person,
  Notes,
  PriorityHigh,
  Notifications,
  Repeat,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { getAuthToken } from '../../lib/authToken';

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  eventType: 'client_meeting' | 'court_hearing' | 'deposition' | 'mediation' | 'consultation' | 'filing_deadline' | 'document_review' | 'billing_review' | 'staff_meeting' | 'training' | 'other';
  startDate: string;
  endDate: string;
  allDay: boolean;
  location: string | null;
  resourceId?: string | null;
  status: 'scheduled' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reminderEnabled: boolean;
  reminderTime: number;
  reminderSent: boolean;
  notes: string | null;
  attendees: any[];
  recurrence: any;
  case: { id: string; caseNumber: string; title: string; } | null;
  client: { id: string; name: string; } | null;
  task: { id: string; title: string; status: string; } | null;
  creator: { id: string; name: string; email: string; } | null;
  createdAt: string | null;
  updatedAt: string | null;
  caseId?: string | null;
  clientId?: string | null;
  taskId?: string | null;
}

interface CalendarDialogProps {
  open: boolean;
  onClose: () => void;
  event?: CalendarEvent | null;
  mode: 'create' | 'view' | 'edit';
  onSave: () => void;
  initialPrefill?: Partial<CalendarEvent> | null;
}

const CalendarDialog: React.FC<CalendarDialogProps> = ({
  open,
  onClose,
  event,
  mode,
  onSave,
  initialPrefill = null,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<CalendarEvent> & { createVideoMeeting?: boolean }>({
    title: '',
    description: '',
    eventType: 'other',
    startDate: '',
    endDate: '',
    allDay: false,
    location: '',
    resourceId: null,
    status: 'scheduled',
    priority: 'medium',
    reminderEnabled: true,
    reminderTime: 15,
    notes: '',
    attendees: [],
    recurrence: null,
    caseId: null,
    clientId: null,
    taskId: null,
    createVideoMeeting: false,
  });

  // Fetch related data for dropdowns
  const { data: casesData } = useQuery({
    queryKey: ['cases'],
    queryFn: async () => {
      const response = await fetch('/api/cases', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch cases');
      return response.json();
    },
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await fetch('/api/clients', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch clients');
      return response.json();
    },
  });

  const { data: tasksData } = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const response = await fetch('/api/tasks', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch tasks');
      return response.json();
    },
  });

  const { data: resourcesData } = useQuery({
    queryKey: ['resources'],
    queryFn: async () => {
      const response = await fetch('/api/resources?seed=true', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch resources');
      return response.json();
    },
    enabled: open,
  });

  const resources = resourcesData?.data?.resources || [];

  // Create/Update mutation
  const saveEventMutation = useMutation({
    mutationFn: async (data: Partial<CalendarEvent>) => {
      const url = mode === 'create' ? '/api/calendar' : `/api/calendar/${event?.id}`;
      const method = mode === 'create' ? 'POST' : 'PUT';
      
      console.log('Sending request to:', url);
      console.log('Request method:', method);
      console.log('Request data:', data);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error('Failed to save event');
      }
      
      const result = await response.json();
      console.log('Response result:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(`Event ${mode === 'create' ? 'created' : 'updated'} successfully`);
      onSave();
    },
    onError: (error) => {
      toast.error(`Failed to ${mode === 'create' ? 'create' : 'update'} event`);
      console.error('Save error:', error);
    },
  });

  useEffect(() => {
    if (event && mode !== 'create') {
      setFormData({
        title: event.title,
        description: event.description,
        eventType: event.eventType,
        startDate: event.startDate ? format(parseISO(event.startDate), "yyyy-MM-dd'T'HH:mm") : '',
        endDate: event.endDate ? format(parseISO(event.endDate), "yyyy-MM-dd'T'HH:mm") : '',
        allDay: event.allDay,
        location: event.location || '',
        resourceId: event.resourceId || null,
        status: event.status,
        priority: event.priority,
        reminderEnabled: event.reminderEnabled,
        reminderTime: event.reminderTime,
        notes: event.notes || '',
        attendees: event.attendees || [],
        recurrence: event.recurrence,
        caseId: event.case?.id || null,
        clientId: event.client?.id || null,
        taskId: event.task?.id || null,
        createVideoMeeting: false,
      });
    } else {
      setFormData({
        title: initialPrefill?.title || '',
        description: initialPrefill?.description || '',
        eventType: initialPrefill?.eventType || 'other',
        startDate: initialPrefill?.startDate || '',
        endDate: initialPrefill?.endDate || '',
        allDay: initialPrefill?.allDay ?? false,
        location: initialPrefill?.location || '',
        resourceId: initialPrefill?.resourceId || null,
        status: 'scheduled',
        priority: 'medium',
        reminderEnabled: true,
        reminderTime: 15,
        notes: initialPrefill?.notes || '',
        attendees: [],
        recurrence: null,
        caseId: null,
        clientId: null,
        taskId: null,
        createVideoMeeting: false,
      });
    }
  }, [event, mode, initialPrefill, open]);

  const handleInputChange = (field: string, value: any) => {
    // Convert empty strings to null for ID fields
    let processedValue = value;
    if (field === 'caseId' || field === 'clientId' || field === 'taskId' || field === 'resourceId') {
      processedValue = value === '' ? null : value;
    }
    setFormData(prev => ({ ...prev, [field]: processedValue }));

    if (field === 'resourceId' && value) {
      const resource = resources.find((r: any) => r.id === value);
      if (resource) {
        setFormData(prev => ({
          ...prev,
          resourceId: value,
          location: resource.location || resource.name || prev.location,
          createVideoMeeting: resource.type === 'zoom_bridge' ? true : prev.createVideoMeeting,
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    console.log('Submitting form data:', formData);
    
    try {
      await saveEventMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: { [key: string]: string } = {
      client_meeting: '#1976d2',
      court_hearing: '#d32f2f',
      deposition: '#ed6c02',
      mediation: '#0288d1',
      consultation: '#2e7d32',
      filing_deadline: '#c62828',
      document_review: '#f57c00',
      billing_review: '#0277bd',
      staff_meeting: '#1565c0',
      training: '#388e3c',
      other: '#757575',
    };
    return colors[eventType] || '#757575';
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: '#4caf50',
      medium: '#ff9800',
      high: '#f44336',
      urgent: '#d32f2f',
    };
    return colors[priority] || '#757575';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      scheduled: '#2196f3',
      confirmed: '#4caf50',
      cancelled: '#f44336',
      completed: '#2e7d32',
      no_show: '#ff9800',
    };
    return colors[status] || '#757575';
  };

  const eventTypeOptions = [
    { value: 'client_meeting', label: 'Client Meeting' },
    { value: 'court_hearing', label: 'Court Hearing' },
    { value: 'deposition', label: 'Deposition' },
    { value: 'mediation', label: 'Mediation' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'filing_deadline', label: 'Filing Deadline' },
    { value: 'document_review', label: 'Document Review' },
    { value: 'billing_review', label: 'Billing Review' },
    { value: 'staff_meeting', label: 'Staff Meeting' },
    { value: 'training', label: 'Training' },
    { value: 'other', label: 'Other' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' },
    { value: 'no_show', label: 'No Show' },
  ];

  const reminderTimeOptions = [
    { value: 0, label: 'No reminder' },
    { value: 5, label: '5 minutes before' },
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 1440, label: '1 day before' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Event />
            <Typography variant="h6">
              {mode === 'create' ? 'Create New Event' : mode === 'edit' ? 'Edit Event' : 'Event Details'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {mode === 'view' && event ? (
        // View Mode - No form needed
        <>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  {event.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={event.eventType.replace('_', ' ')}
                    size="small"
                    sx={{ backgroundColor: getEventTypeColor(event.eventType), color: 'white' }}
                  />
                  <Chip
                    label={event.priority}
                    size="small"
                    sx={{ backgroundColor: getPriorityColor(event.priority), color: 'white' }}
                  />
                  <Chip
                    label={event.status}
                    size="small"
                    sx={{ backgroundColor: getStatusColor(event.status), color: 'white' }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" paragraph>
                  {event.description}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Today />
                  <Typography variant="subtitle2">Start Date</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {event.startDate ? format(parseISO(event.startDate), 'PPpp') : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Schedule />
                  <Typography variant="subtitle2">End Date</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {event.endDate ? format(parseISO(event.endDate), 'PPpp') : 'N/A'}
                </Typography>
              </Grid>

              {event.location && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOn />
                    <Typography variant="subtitle2">Location</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {event.location}
                  </Typography>
                </Grid>
              )}

              {event.creator && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Person />
                    <Typography variant="subtitle2">Created By</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {event.creator.name}
                  </Typography>
                </Grid>
              )}

              {event.notes && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Notes />
                    <Typography variant="subtitle2">Notes</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {event.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        // Create/Edit Mode - Form needed
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Event Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={formData.eventType}
                    onChange={(e) => handleInputChange('eventType', e.target.value)}
                    label="Event Type"
                    disabled={mode === 'view'}
                  >
                    {eventTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    label="Priority"
                    disabled={mode === 'view'}
                  >
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date & Time"
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  required
                  disabled={mode === 'view'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  required
                  disabled={mode === 'view'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location (free text)"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Book resource</InputLabel>
                  <Select
                    label="Book resource"
                    value={formData.resourceId || ''}
                    onChange={(e) => handleInputChange('resourceId', e.target.value)}
                    disabled={mode === 'view'}
                  >
                    <MenuItem value="">None</MenuItem>
                    {resources.map((r: any) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name} ({r.type === 'zoom_bridge' ? 'Zoom/Video' : 'Room'})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {resources.find((r: any) => r.id === formData.resourceId)?.type === 'zoom_bridge' && mode === 'create' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.createVideoMeeting}
                        onChange={(e) => handleInputChange('createVideoMeeting', e.target.checked)}
                      />
                    }
                    label="Create video meeting room (Twilio / Zoom bridge)"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                    disabled={mode === 'view'}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allDay}
                      onChange={(e) => handleInputChange('allDay', e.target.checked)}
                      disabled={mode === 'view'}
                    />
                  }
                  label="All Day Event"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.reminderEnabled}
                      onChange={(e) => handleInputChange('reminderEnabled', e.target.checked)}
                      disabled={mode === 'view'}
                    />
                  }
                  label="Enable Reminder"
                />
              </Grid>

              {formData.reminderEnabled && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Reminder Time</InputLabel>
                    <Select
                      value={formData.reminderTime}
                      onChange={(e) => handleInputChange('reminderTime', e.target.value)}
                      label="Reminder Time"
                      disabled={mode === 'view'}
                    >
                      {reminderTimeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={casesData?.data?.cases || []}
                  getOptionLabel={(option) => `${option.caseNumber} - ${option.title}`}
                  value={casesData?.data?.cases?.find((c: any) => c.id === formData.caseId) || null}
                  onChange={(_, newValue) => handleInputChange('caseId', newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Related Case" />
                  )}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={clientsData?.data?.clients || []}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                  value={clientsData?.data?.clients?.find((c: any) => c.id === formData.clientId) || null}
                  onChange={(_, newValue) => handleInputChange('clientId', newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Related Client" />
                  )}
                  disabled={mode === 'view'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  multiline
                  rows={3}
                  disabled={mode === 'view'}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>
              {mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
              >
                {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Event' : 'Update Event'}
              </Button>
            )}
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default CalendarDialog; 